/**
 * CompanyRegistrations — Review-Workbench für Flow→B Firmen-Registrierungen.
 *
 * Flow pusht Registrierungen (status=waiting_approval); hier entscheidet der Boss
 * accept/reject. Die Entscheidung wird über den /status-Poll an Flow zurückgespiegelt;
 * Firma + external_sync_key entstehen weiterhin in Flow.
 *
 * @page /firmen-registrierungen
 * @api  /api/company-registrations*
 * @i18n companyReg.*
 * @since v2.780.0 / #1609
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, Search, CheckCircle2, XCircle, Clock, Loader2, FileText, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(path, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    let data;
    try { data = await r.json(); } catch { throw new Error(`HTTP ${r.status}`); }
    if (!r.ok) throw new Error(data.message || data.error || `HTTP ${r.status}`);
    return data;
  });
}

const STATUS_META = {
  pending:  { color: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { color: 'bg-rose-50 text-rose-700 border-rose-200' },
};

function StatusBadge({ status, t }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${meta.color}`}>
      {t(`companyReg.status.${status}`) || status}
    </span>
  );
}

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d)) return '—';
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Addr({ a }) {
  if (!a || typeof a !== 'object') return <span className="text-[var(--text-muted)]">—</span>;
  return (
    <span>
      {a.company ? <>{a.company}<br /></> : null}
      {[a.street, a.street2].filter(Boolean).join(', ')}<br />
      {[a.zip, a.city].filter(Boolean).join(' ')} {a.country_code || ''}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-[var(--text-tertiary)] mb-0.5">{label}</div>
      <div className="text-sm text-[var(--text-primary)]">{children ?? <span className="text-[var(--text-muted)]">—</span>}</div>
    </div>
  );
}

function DetailDrawer({ id, onClose, onDecided, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setData(await api(`/api/company-registrations/${id}`)); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const reg = data?.registration;
  const docs = data?.documents || [];

  async function decide(action) {
    setBusy(true); setErr(null);
    try {
      if (action === 'accept') {
        await api(`/api/company-registrations/${id}/accept`, { method: 'POST' });
      } else {
        await api(`/api/company-registrations/${id}/reject`, { method: 'POST', body: { reason } });
      }
      onDecided();
      onClose();
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[var(--border-default)] px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">
            {reg?.company_name || t('companyReg.detail_title')}
          </h2>
          <button onClick={onClose} className="p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#338edc]" /></div>}
        {err && <div className="m-5 p-3 rounded-lg bg-rose-50 text-rose-700 text-sm">{err}</div>}

        {reg && (
          <div className="p-5 space-y-5">
            <div className="flex items-center gap-2">
              <StatusBadge status={reg.status} t={t} />
              {reg.decided_at && (
                <span className="text-xs text-[var(--text-tertiary)]">
                  {fmtDate(reg.decided_at)} · {reg.decided_by_vorname || ''} {reg.decided_by_nachname || ''}
                </span>
              )}
            </div>
            {reg.status === 'rejected' && reg.decision_reason && (
              <div className="p-3 rounded-lg bg-rose-50 text-rose-700 text-sm">
                <strong>{t('companyReg.reason')}:</strong> {reg.decision_reason}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Field label={t('companyReg.field.company_name')}>{reg.company_name}</Field>
              <Field label={t('companyReg.field.warehouse')}>{reg.warehouse}</Field>
              <Field label={t('companyReg.field.email')}>{reg.email}</Field>
              <Field label={t('companyReg.field.username')}>{reg.username}</Field>
              <Field label={t('companyReg.field.tax_id')}>{reg.company_tax_id}</Field>
              <Field label={t('companyReg.field.bin')}>{reg.company_bin}</Field>
              <Field label={t('companyReg.field.payment_terms')}>{reg.payment_terms}</Field>
              <Field label={t('companyReg.field.received_at')}>{fmtDate(reg.received_at)}</Field>
            </div>

            {reg.payment_terms === 'debit' && (
              <div className="grid grid-cols-3 gap-4">
                <Field label={t('companyReg.field.bank_name')}>{reg.bank_name}</Field>
                <Field label="IBAN">{reg.iban}</Field>
                <Field label="BIC">{reg.bic}</Field>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Field label={t('companyReg.field.company_address')}><Addr a={reg.company_address} /></Field>
              <Field label={t('companyReg.field.remission_address')}><Addr a={reg.remission_address} /></Field>
            </div>

            {reg.contact && (
              <Field label={t('companyReg.field.contact')}>
                {reg.contact.name} {reg.contact.position ? `(${reg.contact.position})` : ''}<br />
                {[reg.contact.phone, reg.contact.email].filter(Boolean).join(' · ')}
              </Field>
            )}

            <div>
              <div className="text-xs text-[var(--text-tertiary)] mb-1.5">{t('companyReg.field.documents')}</div>
              {docs.length === 0 && <div className="text-sm text-[var(--text-muted)]">—</div>}
              <div className="space-y-1.5">
                {docs.map((d) => (
                  <a
                    key={d.id}
                    href={`/api/company-registrations/${id}/documents/${d.id}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-[#338edc] hover:underline"
                  >
                    <FileText className="w-4 h-4" />
                    {d.name} <span className="text-[var(--text-tertiary)]">({d.document_type})</span>
                  </a>
                ))}
              </div>
            </div>

            {reg.status === 'pending' && (
              <div className="border-t border-[var(--border-default)] pt-4 space-y-3">
                {!rejecting ? (
                  <div className="flex gap-3">
                    <button
                      disabled={busy}
                      onClick={() => decide('accept')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-4 h-4" /> {t('companyReg.accept')}
                    </button>
                    <button
                      disabled={busy}
                      onClick={() => setRejecting(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" /> {t('companyReg.reject')}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={t('companyReg.reason_placeholder')}
                      rows={3}
                      className="w-full text-sm border border-[var(--border-default)] rounded-lg px-3 py-2"
                    />
                    <div className="flex gap-3">
                      <button
                        disabled={busy || reason.trim().length < 3}
                        onClick={() => decide('reject')}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" /> {t('companyReg.confirm_reject')}
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => { setRejecting(false); setReason(''); }}
                        className="px-4 py-2 rounded-lg border border-[var(--border-default)] text-sm text-[var(--text-secondary)]"
                      >
                        {t('companyReg.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CompanyRegistrations() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'alle') params.set('status', statusFilter);
      if (q) params.set('q', q);
      const data = await api(`/api/company-registrations?${params}`);
      setItems(data.registrations || []);
      setStats(data.stats || null);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    if (!stats) return null;
    return [
      { key: 'pending', label: t('companyReg.kpi.pending'), value: stats.pending || 0, icon: Clock, color: 'text-amber-600' },
      { key: 'approved', label: t('companyReg.kpi.approved'), value: stats.approved || 0, icon: CheckCircle2, color: 'text-emerald-600' },
      { key: 'rejected', label: t('companyReg.kpi.rejected'), value: stats.rejected || 0, icon: XCircle, color: 'text-rose-600' },
    ];
  }, [stats, t]);

  const filters = ['alle', 'pending', 'approved', 'rejected'];

  return (
    <div className="px-6 py-6 space-y-4">
      <div className="flex items-center gap-2">
        <Building2 className="w-6 h-6 text-[#338edc]" />
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{t('companyReg.page_title')}</h1>
      </div>

      {kpis && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {kpis.map((k) => {
            const Ic = k.icon;
            return (
              <div key={k.key} className="border border-[var(--border-default)] rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs text-[var(--text-tertiary)]">{k.label}</div>
                  <div className="text-2xl font-semibold text-[var(--text-primary)]">{k.value}</div>
                </div>
                <Ic className={`w-6 h-6 ${k.color}`} />
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default)]">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1 text-sm rounded-[8px] ${statusFilter === f ? 'bg-white shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}
            >
              {t(`companyReg.filter.${f}`) || f}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('companyReg.search_placeholder')}
            className="pl-8 pr-3 py-1.5 text-sm border border-[var(--border-default)] rounded-lg"
          />
        </div>
      </div>

      {err && <div className="p-3 rounded-lg bg-rose-50 text-rose-700 text-sm">{err}</div>}

      <div className="border border-[var(--border-default)] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">{t('companyReg.col.company')}</th>
              <th className="text-left font-medium px-4 py-2.5">{t('companyReg.col.email')}</th>
              <th className="text-left font-medium px-4 py-2.5">{t('companyReg.col.warehouse')}</th>
              <th className="text-left font-medium px-4 py-2.5">{t('companyReg.col.payment')}</th>
              <th className="text-left font-medium px-4 py-2.5">{t('companyReg.col.status')}</th>
              <th className="text-left font-medium px-4 py-2.5">{t('companyReg.col.received')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#338edc] inline" /></td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--text-muted)]">{t('companyReg.empty')}</td></tr>
            )}
            {!loading && items.map((r) => (
              <tr
                key={r.id}
                onClick={() => setSelectedId(r.id)}
                className="border-t border-[var(--border-default)] hover:bg-[var(--bg-inset,#F8F9FC)] cursor-pointer"
              >
                <td className="px-4 py-2.5 text-[var(--text-primary)] font-medium">{r.company_name || '—'}</td>
                <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.email || '—'}</td>
                <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.warehouse || '—'}</td>
                <td className="px-4 py-2.5 text-[var(--text-secondary)]">{r.payment_terms || '—'}</td>
                <td className="px-4 py-2.5"><StatusBadge status={r.status} t={t} /></td>
                <td className="px-4 py-2.5 text-[var(--text-tertiary)]">{fmtDate(r.received_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <DetailDrawer id={selectedId} t={t} onClose={() => setSelectedId(null)} onDecided={load} />
      )}
    </div>
  );
}
